import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import { CloseIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { FormInput } from '../../../../../../components/input/index';
import Loader from '../../../../../../components/loader/index';
import { ScenarioHooks, useDebounce } from '../../../../../../hooks/index';
import { CreateScenarioPool } from '../../../../../../types/scenario-modeling.types';
import { toRound } from '../../../../../../utils/get-rounded-number.util';
import { toNumber } from '../../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { FundraisingFormSchema, investmentsFormSchema } from '../../../fundraising-validation';
import { StakeholderCombobox, TableHeaderItem } from '../form-components/index';
import { ShareClassCombobox } from '../form-components/share-class/combobox';
import { NewItemListProps } from './items.types';

export type InvestmentItemProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  index: number;
  onRemoveItem: () => void;
  scenarioId: string;
  setIsPending: (isPending: boolean) => void;
  sharePrice: number;
  skipUpdateIdsRef: React.MutableRefObject<Set<string>>;
};

export const InvestmentItem: FC<InvestmentItemProps> = ({
  control,
  index,
  companyId,
  scenarioId,
  onRemoveItem,
  skipUpdateIdsRef,
  setIsPending,
  sharePrice,
}) => {
  const { investments } = useWatch<FundraisingFormSchema>({ control });
  const { success: investmentSuccess, error } = investmentsFormSchema
    .superRefine((data, ctx) => {
      data.investmentItems.forEach((item, index) => {
        if (item.investment < sharePrice) {
          ctx.addIssue({
            code: 'custom',
            message: 'Investment must be more or equal share price',
            path: ['investmentItems', index, 'investment'],
          });
        }
      });
    })
    .safeParse(investments);

  const investmentItem = investments?.investmentItems?.[index];
  const lastSentRef = useRef<CreateScenarioPool | null>(null);
  const { scheduleUpdate } = useScenarioCapTableStore();
  const { createScenarioInvestment, isPending } = ScenarioHooks.useCreateScenarioInvestment();
  const { updateScenarioInvestment, isPending: isUpdatePending } =
    ScenarioHooks.usePatchScenarioInvestment();
  const { deleteScenarioInvestment } = ScenarioHooks.useDeleteScenarioInvestment();
  const { data } = ScenarioHooks.useCalcScenarioInvestment({
    scenarioId,
    investmentId: investmentItem?.id || '',
  });

  const { pointerBlockedStyles } = useScenarioStore();

  const handleRemoveItem = () => {
    if (investmentItem?.id) {
      scheduleUpdate(scenarioId);
      skipUpdateIdsRef.current.add(investmentItem.id);
      return deleteScenarioInvestment(
        { investmentId: investmentItem.id, scenarioId },
        {
          onSuccess: () => {
            onRemoveItem();
            lastSentRef.current = null;

            setTimeout(() => {
              if (investmentItem?.id) {
                skipUpdateIdsRef.current.delete(investmentItem.id);
              }
            }, 1000);
          },
        },
      );
    }
    onRemoveItem();
    lastSentRef.current = null;
  };

  const extractInvestmentSnapshot = useCallback(
    (item: typeof investmentItem) => ({
      investment: toNumber(item?.investment),
      shareClassId: item?.shareClass?.id || '',
      stakeholderId: item?.stakeholder?.id === 'new' ? undefined : item?.stakeholder?.id,
      fakeStakeholderName: item?.stakeholder?.id === 'new' ? item?.stakeholder.fullName : undefined,
    }),
    [],
  );

  const { debounceCallback } = useDebounce<typeof investmentItem>({
    debounce: 500,
    callback: (item) => {
      if (!item || !investmentSuccess) return;
      if (item.id && skipUpdateIdsRef.current.has(item.id)) return;

      const investmentSnapshot = extractInvestmentSnapshot(item);
      const { id, shareClass, investment, stakeholder } = item;

      if (JSON.stringify(investmentSnapshot) === JSON.stringify(lastSentRef.current)) return;

      if (!id) {
        scheduleUpdate(scenarioId);
        createScenarioInvestment({
          scenarioId,
          data: {
            investment: toNumber(investment),
            shareClassId: shareClass?.id || '',
            stakeholderId: stakeholder?.id === 'new' ? undefined : stakeholder?.id,
            fakeStakeholderName: stakeholder?.id === 'new' ? stakeholder?.fullName : undefined,
          },
        });
      } else {
        scheduleUpdate(scenarioId);
        updateScenarioInvestment({
          scenarioId,
          investmentId: id,
          data: {
            investment: toNumber(investment),
            shareClassId: shareClass?.id || '',
            stakeholderId: stakeholder?.id === 'new' ? undefined : stakeholder?.id,
            fakeStakeholderName: stakeholder?.id === 'new' ? stakeholder.fullName : undefined,
          },
        });
      }
      lastSentRef.current = investmentSnapshot;
    },
  });

  useEffect(() => {
    if (!investmentItem || (investmentItem?.id && skipUpdateIdsRef.current.has(investmentItem?.id)))
      return;

    const currentSnapshot = extractInvestmentSnapshot(investmentItem);
    if (lastSentRef.current === null) {
      lastSentRef.current = currentSnapshot;
      return;
    }

    if (JSON.stringify(currentSnapshot) === JSON.stringify(lastSentRef.current)) return;

    debounceCallback(investmentItem);
  }, [debounceCallback, skipUpdateIdsRef, investmentItem, extractInvestmentSnapshot]);

  useEffect(() => {
    if (isPending || isUpdatePending) {
      setIsPending(true);
    } else {
      const timeout = setTimeout(() => {
        setIsPending(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isPending, isUpdatePending, setIsPending]);

  return (
    <>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <StakeholderCombobox
          companyId={companyId}
          control={control}
          fieldName={`investments.investmentItems.${index}.stakeholder`}
          scenarioId={scenarioId}
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          currencySign
          errorMessage={
            investments?.investmentItems?.[index]?.investment &&
            error?.issues.find(
              (e) =>
                JSON.stringify(e.path) === JSON.stringify(['investmentItems', index, 'investment']),
            )?.message
          }
          inputClassName="h-10"
          maxDecimalPlaces={2}
          name={`investments.investmentItems.${index}.investment`}
          numberOnly
          shouldFormatNumber
        />
      </div>
      <div className={twJoin('flex items-center px-2 py-4', pointerBlockedStyles)}>
        <ShareClassCombobox
          companyId={companyId}
          control={control}
          name={`investments.investmentItems.${index}.shareClass`}
        />
      </div>
      <div className="flex w-full items-center justify-center px-2 py-4">
        <span className="w-full rounded-t-[4px] bg-gray-50 px-3 py-[10px] text-sm font-[450] text-gray-700">
          {data?.issuedShares ? data?.issuedShares?.toLocaleString('en-US') : '-'}
        </span>
      </div>
      <div className="flex w-full items-center justify-center px-2 py-4">
        <span className="w-full rounded-t-[4px] bg-gray-50 px-3 py-[10px] text-sm font-[450] text-gray-700">
          {data?.ownership ? `${toRound(data.ownership * 100, 2)}%` : '-'}
        </span>
      </div>
      <div className={twMerge('flex h-full items-center justify-center', pointerBlockedStyles)}>
        <Button
          className="w flex h-full w-full items-center justify-center"
          onClick={handleRemoveItem}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
      </div>
    </>
  );
};

export const InvestmentItemList: FC<NewItemListProps> = ({
  control,
  companyId,
  columns,
  addButtonText,
  scenarioId,
}) => {
  const { sharePrice } = useScenarioStore();
  const [isPending, setIsPending] = useState<boolean>(false);
  const { investments } = useWatch<FundraisingFormSchema>({ control });
  const { success: investmentsSuccess } = investmentsFormSchema
    .superRefine((data, ctx) => {
      data.investmentItems.forEach((item, index) => {
        if (item.investment < sharePrice) {
          ctx.addIssue({
            code: 'custom',
            message: 'Investment must be more or equal share price',
            path: ['investmentItems', index, 'investment'],
          });
        }
      });
    })
    .safeParse(investments);

  const skipUpdateIdsRef = useRef<Set<string>>(new Set());
  const {
    fields: itemsFields,
    append: itemsAppend,
    remove: itemsRemove,
  } = useFieldArray({
    control,
    name: 'investments.investmentItems',
  });

  const handleRemoveItems = useCallback(
    (index: number) => {
      itemsRemove(index);
    },
    [itemsRemove],
  );

  const handleAppendItems = useCallback(() => {
    itemsAppend({
      investment: 0,
      stakeholder: { fullName: '', id: '' },
      shareClass: { id: '', name: '' },
    });
  }, [itemsAppend]);

  return (
    <div
      className={twMerge(
        'relative flex w-full min-w-[1520px] flex-col gap-3',
        isPending ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {isPending && <Loader className="absolute z-10" />}
      {itemsFields.length > 0 && (
        <div className="flex min-w-[1520px] flex-col rounded-lg border-[1px] border-gray-100 bg-white">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(5, minmax(170px, 1fr)) 48px`,
            }}
          >
            {columns.map((column, index) => (
              <TableHeaderItem key={index} text={column} />
            ))}
            <TableHeaderItem text="" />
          </div>
          {itemsFields.map((field, index) => (
            <Fragment key={field.id}>
              <div
                className="grid items-start border-b-[1px] border-b-gray-100"
                key={field.id}
                style={{
                  gridTemplateColumns: `repeat(5, minmax(170px, 1fr)) 48px`,
                }}
              >
                <InvestmentItem
                  companyId={companyId}
                  control={control}
                  index={index}
                  onRemoveItem={() => handleRemoveItems(index)}
                  scenarioId={scenarioId}
                  setIsPending={setIsPending}
                  sharePrice={sharePrice}
                  skipUpdateIdsRef={skipUpdateIdsRef}
                />
              </div>
            </Fragment>
          ))}
        </div>
      )}

      <div className="w-full bg-white">
        <Button
          className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent disabled:ring-0"
          disabled={itemsFields?.length > 0 && !investmentsSuccess}
          onClick={handleAppendItems}
          styleType="NONE"
        >
          + <AppFormattedMessage id="ADD" /> {addButtonText}
        </Button>
      </div>
    </div>
  );
};
